import { api } from '@/api/client'
import type {
  AuthResult,
  ChangePasswordPayload,
  CurrentUser,
  AuthProviders,
  LoginMethods,
  LoginPayload,
  LoginResult,
  RegisterPayload,
  ResetPasswordPayload,
  Session,
  TwoFactorSetup,
  TwoFactorStatus,
  UpdateProfilePayload,
} from '@/types/auth'

export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'me'] as const,
  sessions: () => [...authKeys.all, 'sessions'] as const,
  providers: () => [...authKeys.all, 'providers'] as const,
  loginMethods: () => [...authKeys.all, 'login-methods'] as const,
  twoFactor: () => [...authKeys.all, 'two-factor'] as const,
}

export const authApi = {
  register: (payload: RegisterPayload) =>
    api.post<void>('/api/auth/register', payload, { anonymous: true }),

  confirmEmail: (userId: string, token: string) =>
    api.post<void>('/api/auth/confirm-email', { userId, token }, { anonymous: true }),

  resendConfirmation: (email: string) =>
    api.post<void>('/api/auth/resend-confirmation', { email }, { anonymous: true }),

  /**
   * Første trin. Svarer enten med en session eller med et krav om en
   * engangskode — se `LoginResult`.
   */
  login: (payload: LoginPayload) =>
    api.post<LoginResult>('/api/auth/login', payload, { anonymous: true }),

  /** Andet trin: billetten fra loginsvaret plus koden fra appen. */
  verifyTwoFactor: (payload: { ticket: string; code: string; rememberMe: boolean }) =>
    api.post<AuthResult>('/api/auth/two-factor', payload, { anonymous: true }),

  twoFactorStatus: () => api.get<TwoFactorStatus>('/api/auth/two-factor'),

  /** Hvilke udbydere der er sat op. Tom liste betyder: vis ingen knapper. */
  providers: () => api.get<AuthProviders>('/api/auth/providers', { anonymous: true }),

  loginMethods: () => api.get<LoginMethods>('/api/auth/login-methods'),

  unlinkExternal: (provider: string) => api.delete<void>(`/api/auth/external/${provider}`),

  /**
   * Turen til Microsoft er en almindelig navigation og ikke et fetch-kald:
   * browseren skal hen til Microsoft og hjem igen, og undervejs sætter
   * serveren de cookies, sessionen består af.
   */
  startExternal: (provider: string, options: { returnTo?: string; link?: boolean } = {}) => {
    const query = new URLSearchParams()
    if (options.returnTo) query.set('returnTo', options.returnTo)
    if (options.link) query.set('link', 'true')

    window.location.href = `/api/auth/external/${provider}/start?${query.toString()}`
  },

  /** Udleverer en frisk nøgle. Tofaktor slås først til med `enableTwoFactor`. */
  beginTwoFactorSetup: () => api.post<TwoFactorSetup>('/api/auth/two-factor/setup'),

  enableTwoFactor: (payload: { password: string; code: string }) =>
    api.post<{ codes: string[] }>('/api/auth/two-factor/enable', payload),

  disableTwoFactor: (payload: { password: string; code: string }) =>
    api.post<void>('/api/auth/two-factor/disable', payload),

  regenerateRecoveryCodes: (payload: { password: string; code: string }) =>
    api.post<{ codes: string[] }>('/api/auth/two-factor/recovery-codes', payload),

  /**
   * Fornyer sessionen ud fra refresh token-cookien. Kaldes anonymt, fordi det
   * gamle access token typisk allerede er udløbet.
   */
  refresh: () => api.post<AuthResult>('/api/auth/refresh', undefined, { anonymous: true }),

  logout: () => api.post<void>('/api/auth/logout'),

  logoutAllDevices: () => api.post<{ revokedSessions: number }>('/api/auth/logout-all'),

  listSessions: () => api.get<Session[]>('/api/auth/sessions'),

  revokeSession: (sessionId: string) => api.delete<void>(`/api/auth/sessions/${sessionId}`),

  forgotPassword: (email: string) =>
    api.post<void>('/api/auth/forgot-password', { email }, { anonymous: true }),

  resetPassword: (payload: ResetPasswordPayload) =>
    api.post<void>('/api/auth/reset-password', payload, { anonymous: true }),

  changePassword: (payload: ChangePasswordPayload) =>
    api.post<void>('/api/auth/change-password', payload),

  switchOrganization: (organizationId: string) =>
    api.post<AuthResult>('/api/auth/switch-organization', { organizationId }),

  getCurrentUser: () => api.get<CurrentUser>('/api/users/me'),

  updateProfile: (payload: UpdateProfilePayload) =>
    api.patch<CurrentUser>('/api/users/me', payload),

  /** Gemmer temavalget på brugeren, så det følger med til næste enhed. */
  updateTheme: (theme: 'light' | 'dark') =>
    api.put<void>('/api/users/me/theme', { theme }),
}
